import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertCircle, CheckCircle, Mail, Pen, Clock, Info } from 'lucide-react';

const VIUBUX_LOGO = 'https://customer-assets.emergentagent.com/job_libro-quote-gen/artifacts/pf9umyn8_5.png';

export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f133a] to-[#1a1f50]">
      {/* Header */}
      <header className="bg-[#0f133a]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src={VIUBUX_LOGO} 
                alt="Viubux" 
                className="h-8 w-auto"
              />
              <span className="text-white font-bold text-lg">Viubux</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Volver al Cotizador</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {/* Title Section */}
          <div className="bg-gradient-to-r from-[#aa2aff] to-[#272b96] p-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Términos y Condiciones
                </h1>
                <p className="text-white/80 mt-1">
                  Última actualización: {new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>
          </div>

          {/* Terms Content */}
          <div className="p-8 space-y-8 text-slate-300">
            {/* Important Notice */}
            <section className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-amber-500/20 rounded-lg flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-amber-400 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Aviso Importante
                  </h2>
                  <p className="text-lg text-white">
                    Esta cotización <strong>NO ES FINAL</strong> y sirve únicamente para proporcionar un 
                    <strong> estimado</strong> basado en la información proporcionada.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 1 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                  <Info className="w-5 h-5 text-[#aa2aff]" />
                </div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  1. Naturaleza de la Cotización
                </h2>
              </div>
              <div className="pl-11 space-y-3">
                <p>
                  La cotización generada a través de esta plataforma es un <strong className="text-white">estimado preliminar</strong> 
                  que tiene como objetivo brindarte una idea aproximada de los costos de producción audiovisual 
                  basándose en la información que has proporcionado.
                </p>
                <div className="bg-[#0f133a] rounded-lg p-4 border border-white/10">
                  <p className="text-slate-400 italic">
                    Los precios, tiempos de entrega y características del servicio pueden variar dependiendo de 
                    los requerimientos específicos de cada proyecto una vez que se analice en detalle.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 2 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                  <Pen className="w-5 h-5 text-[#aa2aff]" />
                </div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  2. Diseño de Producción
                </h2>
              </div>
              <div className="pl-11 space-y-3">
                <p>
                  Esta plataforma tiene la intención de recibir un <strong className="text-white">diseño de producción</strong> que 
                  permita obtener información más detallada sobre lo que deseas producir. Este diseño nos ayudará a:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Entender mejor tu visión creativa</li>
                  <li>Definir los requisitos técnicos específicos</li>
                  <li>Establecer un cronograma de trabajo realista</li>
                  <li>Proporcionar una cotización final más precisa</li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                  <Mail className="w-5 h-5 text-[#aa2aff]" />
                </div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  3. Proceso de Confirmación
                </h2>
              </div>
              <div className="pl-11 space-y-4">
                <p>
                  Los detalles finales de la producción serán confirmados mediante el siguiente proceso:
                </p>
                <div className="grid gap-4">
                  <div className="flex items-start gap-4 bg-[#0f133a] rounded-lg p-4 border border-white/10">
                    <div className="p-2 bg-[#aa2aff]/20 rounded-full flex-shrink-0">
                      <span className="text-[#aa2aff] font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Revisión del Proyecto</h4>
                      <p className="text-sm text-slate-400">Analizaremos tu diseño de producción y requerimientos específicos.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 bg-[#0f133a] rounded-lg p-4 border border-white/10">
                    <div className="p-2 bg-[#aa2aff]/20 rounded-full flex-shrink-0">
                      <span className="text-[#aa2aff] font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Cotización Final</h4>
                      <p className="text-sm text-slate-400">Te enviaremos una cotización ajustada con todos los detalles del proyecto.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 bg-[#0f133a] rounded-lg p-4 border border-white/10">
                    <div className="p-2 bg-[#aa2aff]/20 rounded-full flex-shrink-0">
                      <span className="text-[#aa2aff] font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Firma Digital</h4>
                      <p className="text-sm text-slate-400">La producción será confirmada mediante firma digital a través de correo electrónico.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-[#aa2aff]" />
                </div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  4. Confirmación de Producción
                </h2>
              </div>
              <div className="pl-11">
                <div className="bg-gradient-to-r from-[#aa2aff]/20 to-[#272b96]/20 border border-[#aa2aff]/30 rounded-lg p-6">
                  <p className="text-white text-lg mb-4">
                    La producción será oficialmente confirmada únicamente cuando:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <span className="text-[#aa2aff] text-xl">✓</span>
                      <span>Se haya recibido y aprobado el diseño de producción</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-[#aa2aff] text-xl">✓</span>
                      <span>Se haya acordado la cotización final</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-[#aa2aff] text-xl">✓</span>
                      <span>Se haya firmado digitalmente el contrato vía correo electrónico</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-[#aa2aff] text-xl">✓</span>
                      <span>Se haya recibido el anticipo correspondiente (cuando aplique)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                  <Clock className="w-5 h-5 text-[#aa2aff]" />
                </div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  5. Vigencia de la Cotización
                </h2>
              </div>
              <div className="pl-11">
                <p>
                  Las cotizaciones generadas a través de esta plataforma tienen una vigencia de 
                  <strong className="text-white"> 30 días naturales</strong> a partir de su fecha de emisión. 
                  Pasado este período, los precios y condiciones están sujetos a cambios sin previo aviso.
                </p>
              </div>
            </section>

            {/* Acceptance */}
            <section className="bg-[#0f133a] rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-bold text-white mb-3">Aceptación de Términos</h3>
              <p className="text-slate-400">
                Al utilizar esta plataforma y generar una cotización, aceptas estos términos y condiciones. 
                Si tienes alguna pregunta o requieres aclaraciones, no dudes en contactarnos antes de proceder.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-[#0f133a] rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-bold text-white mb-3">Contacto</h3>
              <p className="text-slate-400 mb-4">
                Para cualquier consulta sobre estos términos:
              </p>
              <div className="space-y-2 text-slate-300">
                <p>Email: <a href="mailto:contacto@viubux.com" className="text-[#aa2aff]">contacto@viubux.com</a></p>
                <p>Teléfono: <a href="tel:+525548564696" className="text-[#aa2aff]">+52 55 4856 4696</a></p>
                <p>Sitio web: <a href="https://www.viubux.com" target="_blank" rel="noopener noreferrer" className="text-[#aa2aff]">www.viubux.com</a></p>
              </div>
            </section>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Volver al Cotizador de Servicios</span>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0f133a]/80 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Viubux. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
